<script setup lang="ts">
import {
    deleteAssetBooking,
    updateAssetBooking,
    type DirectusAssetBooking,
} from '@/api/calls/directus-calls/asset-bookings-calls'
import type AssetBooking from '@/classes/assetBooking'
import Charger from '@/classes/charger_classes/charger'
import type EVSE from '@/classes/charger_classes/evse'
import type Vehicle from '@/classes/vehicle_classes/vehicle'
import AlertPopupWindow from '@/components/alert-popup/AlertPopupWindow.vue'
import DeviceCard from '@/components/charging-stations/DeviceCard.vue'
import GenericDialog from '@/components/dialog-wrappers/GenericDialog.vue'
import ElevatedBlockBtn from '@/components/ui-elements/buttons/ElevatedBlockBtn.vue'
import ElevatedBtn from '@/components/ui-elements/buttons/ElevatedBtn.vue'
import OutlinedBlockBtn from '@/components/ui-elements/buttons/OutlinedBlockBtn.vue'
import OutlinedBtn from '@/components/ui-elements/buttons/OutlinedBtn.vue'
import DatePickerInput, {
    type DatePickerInputUpdateObj,
} from '@/components/ui-elements/inputs/DatePickerInput.vue'
import TimePickerInput, {
    type TimePickerInputUpdateObj,
} from '@/components/ui-elements/inputs/TimePickerInput.vue'
import { cancelBookingAlertData } from '@/data/const'
import { GettersTypes, MutationTypes, type State } from '@/store/store_types'
import { DateTime } from 'luxon'
import { computed, ref } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'

// hooks

const store = useStore<State>()
const { mobile } = useDisplay()

// data

const booking = computed<AssetBooking | undefined>(
  () => store.getters[GettersTypes.getDisplayedBooking],
)

const evseData = computed<EVSE | undefined>(() => {
  if (
    booking.value &&
    booking.value.asset_type === 'charger' &&
    booking.value.asset_reference instanceof Charger
  ) {
    const evse = (booking.value.asset_reference as Charger).evses.find(
      (evse) => evse.uid === booking.value!.asset_id,
    )
    return evse
  }
  return undefined
})

const isMultiDay = computed(() => {
  if (booking.value) {
    const startDate = booking.value.start_date
    const endDate = booking.value.end_date
    const sameDay = startDate.toDateString() === endDate.toDateString()
    return !sameDay
  }
  return false
})

// event handlers

const showCancelConfirmationDialog = ref(false)
const cancelling = ref(false)
const cancelBooking = async () => {
  showCancelConfirmationDialog.value = false
  if (cancelling.value) return
  cancelling.value = true
  if (booking.value && booking.value.external_id) {
    await deleteAssetBooking(booking.value.external_id)
    store.commit(MutationTypes.deleteBooking, booking.value)
    store.commit(MutationTypes.setDashboardDialogContent, 'upcoming-bookings')
  }
  cancelling.value = false
}

const showEditDialog = ref(false)
const newBookingStart = ref<Date | null>(null)
const newBookingEnd = ref<Date | null>(null)
const dirty = ref(false)
const updating = ref(false)
const error = ref<string | null>(null)

const handleUpdateDate = (val: DatePickerInputUpdateObj) => {
  // booking to update exists guard clause
  if (!booking.value) return
  // check if date was passed in as null guard clause
  if (!val.date) {
    if (val.identifier === 'start') newBookingStart.value = null
    else newBookingEnd.value = null
    // check if still dirty
    checkIfDirty()
    return
  }
  // convert js Dates to luxon DateTimes
  const luxonOldDate = DateTime.fromJSDate(
    val.identifier === 'start' ? booking.value!.start_date : booking.value!.end_date,
  )
  const luxonNewDate = DateTime.fromJSDate(val.date)

  // update date portion of DateTime
  if (val.identifier === 'start')
    newBookingStart.value = luxonOldDate
      .set({ day: luxonNewDate.day, month: luxonNewDate.month, year: luxonNewDate.year })
      .toJSDate()
  else
    newBookingEnd.value = luxonOldDate
      .set({ day: luxonNewDate.day, month: luxonNewDate.month, year: luxonNewDate.year })
      .toJSDate()
  validate()
  checkIfDirty()
}

const handleUpdateTime = (val: TimePickerInputUpdateObj) => {
  // booking to update exists guard clause
  if (!booking.value) {
    checkIfDirty()
    return
  }
  // convert js Date to luxon DateTime
  const luxonDate = DateTime.fromJSDate(
    val.identifier === 'start' ? booking.value!.start_date : booking.value!.end_date,
  )

  // update time portion of DateTime
  const regex = '^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$'
  const newTime = val.time.trim().match(regex)
  if (newTime) {
    const newDateTime = luxonDate.set({ hour: parseInt(newTime[1]), minute: parseInt(newTime[2]) })
    if (val.identifier === 'start') newBookingStart.value = newDateTime.toJSDate()
    else newBookingEnd.value = newDateTime.toJSDate()
  }
  validate()
  checkIfDirty()
}

const validate = () => {
  if (!booking.value) {
    error.value = 'Booking does not exist'
    return
  }

  const startDate = newBookingStart.value ?? booking.value!.start_date
  const endDate = newBookingEnd.value ?? booking.value!.end_date

  if (startDate > endDate) {
    error.value = 'The start of the booking must be before the end of the booking'
    return
  }

  if (startDate < new Date()) {
    error.value = 'The start of the booking must be in the future'
    return
  }

  if (endDate < new Date()) {
    error.value = 'The end of the booking must be in the future'
    return
  }

  if (startDate === endDate) {
    error.value = 'The start and end of the booking must be different'
    return
  }

  error.value = null
}

const checkIfDirty = () => {
  // booking to update exists guard clause
  if (!booking.value) {
    dirty.value = false
    return
  }

  // check if no values to change to have been provided
  if (newBookingEnd.value === null && newBookingStart.value === null) {
    dirty.value = false
    return
  }

  const luxonOldStartDate = DateTime.fromJSDate(booking.value!.start_date)
  const luxonOldEndDate = DateTime.fromJSDate(booking.value!.end_date)
  const luxonNewStartDate: DateTime | null = !!newBookingStart.value
    ? DateTime.fromJSDate(newBookingStart.value)
    : null
  const luxonNewEndDate: DateTime | null = !!newBookingEnd.value
    ? DateTime.fromJSDate(newBookingEnd.value)
    : null

  // check if no changes have been made
  if (
    luxonNewStartDate &&
    luxonNewStartDate.equals(luxonOldStartDate) &&
    luxonNewEndDate &&
    luxonNewEndDate.equals(luxonOldEndDate)
  ) {
    dirty.value = false
    return
  }

  // check if only start date has been changed but still matches while end date has not been touched
  if (luxonNewStartDate && !luxonNewEndDate && luxonNewStartDate.equals(luxonOldStartDate)) {
    dirty.value = false
    return
  }

  // check if only end date has been changed but still matches while start date has not been touched
  if (luxonNewEndDate && !luxonNewStartDate && luxonNewEndDate.equals(luxonOldEndDate)) {
    dirty.value = false
    return
  }

  dirty.value = true
}

const handleUpdateBooking = async () => {
  if (updating.value) return
  updating.value = true
  if (!booking.value) return
  if (!booking.value!.external_id) return
  if (newBookingEnd.value || newBookingStart.value) {
    const partialObj: Partial<DirectusAssetBooking> = {}
    if (newBookingStart.value) partialObj.start = newBookingStart.value.toISOString()
    if (newBookingEnd.value) partialObj.end = newBookingEnd.value.toISOString()
    const res = await updateAssetBooking(booking.value!.external_id!, partialObj)
    if (res) {
      booking.value.updateFromDirectusData(res)
    }
    cleanUpEditBooking()
  }
  updating.value = false
}

const cleanUpEditBooking = () => {
  showEditDialog.value = false
  newBookingStart.value = null
  newBookingEnd.value = null
  dirty.value = false
  error.value = null
}
</script>
<template>
  <v-card flat class="bg-pwt_snow py-10">
    <!-- vehicle booking -->
    <div v-if="booking && booking.asset_type === 'vehicle'" class="pa-4">
      <!-- vehicle card -->
      <v-card rounded="lg" class="d-flex align-center pa-4" elevation="3" color="pwt_snow">
        <!-- vehicle image column -->
        <v-col cols="12" md="2">
          <v-img
            contain
            :src="booking.displayImgSrc"
            aspect-ratio="1"
            :width="mobile ? '40%' : '100%'"
            :height="mobile ? 'auto' : '100%'"
            style="margin: auto"
          ></v-img>
        </v-col>
        <!-- vehicle details column -->
        <v-col cols="12" md="7">
          <v-card-title class="pb-0">
            {{ (booking.asset_reference as Vehicle | undefined)?.licensePlate }}
          </v-card-title>
          <v-card-subtitle class="pt-0">
            {{ (booking.asset_reference as Vehicle | undefined)?.name }}
          </v-card-subtitle>
          <v-row no-gutters class="px-4 mt-2">
            <v-col cols="4" class="d-flex align-center">
              <v-icon color="primary" size="small" class="mr-1">
                {{ (booking.asset_reference as Vehicle | undefined)?.fuelTypeIcon }}
              </v-icon>
              {{ (booking.asset_reference as Vehicle | undefined)?.fuelTypeDisplayText }}
            </v-col>
            <v-col cols="4" class="d-flex align-center">
              <v-icon color="primary" size="small" class="mr-1">
                {{ (booking.asset_reference as Vehicle | undefined)?.fuelCapacityIcon }}
              </v-icon>
              {{ (booking.asset_reference as Vehicle | undefined)?.fuelCapacityDisplayText }}
            </v-col>
            <v-col
              v-if="(booking.asset_reference as Vehicle | undefined)?.bootCapacityText"
              cols="4"
              class="d-flex align-center"
            >
              <v-icon color="primary" size="small" class="mr-1">
                mdi-package-variant-closed
              </v-icon>
              {{ (booking.asset_reference as Vehicle | undefined)?.bootCapacityText }}
            </v-col>
          </v-row>
        </v-col>
        <!-- booking actions column -->
        <v-col cols="12" md="3">
          <OutlinedBlockBtn @click="() => (showEditDialog = true)"> Edit booking </OutlinedBlockBtn>
          <v-btn
            class="mt-2 text-none rounded-pill bg-pwt_pink_20"
            color="pwt_red"
            block
            variant="text"
            :loading="cancelling"
            @click="() => (showCancelConfirmationDialog = true)"
          >
            Cancel booking
          </v-btn>
        </v-col>
      </v-card>
      <!-- booking details -->
      <v-card rounded="lg" class="pa-4 mt-5" elevation="3" color="pwt_snow">
        <!-- booking date -->
        <v-card-title> {{isMultiDay ? booking.formattedStartDate + ' - ' + booking.formattedEndDate : booking.formattedStartDate }} </v-card-title>
        <v-row no-gutters>
          <!-- booking  start time column -->
          <v-col cols="6" class="pr-2">
            <v-card color="pwt_alabaster" flat rounded="lg">
              <v-card-text class="d-flex flex-column">
                <span class="text-caption"> Booking starts at </span>
                <span class="text-body-1"> {{ booking.formattedStartTime }} </span>
              </v-card-text>
            </v-card>
          </v-col>
          <!-- booking end time column -->
          <v-col cols="6" class="pl-2">
            <v-card color="pwt_alabaster" flat rounded="lg">
              <v-card-text class="d-flex flex-column">
                <span class="text-caption"> Booking ends at </span>
                <span class="text-body-1"> {{ booking.formattedEndTime }} </span>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <!-- booking extra details section  -->
      </v-card>
    </div>
    <!-- charger booking -->
    <div v-if="booking && booking.asset_type === 'charger'">
      <v-card rounded="lg" class="pa-4 ma-2 mb-5" elevation="3" color="pwt_snow">
        <div class="d-flex flex-column flex-md-row align-md-center align-start">
          <div class="flex-grow-1 flex-shrink-1">
            <v-card-title class="py-0">
              {{ (booking.asset_reference as Charger).name }}
            </v-card-title>
            <v-card-subtitle opacity="1">
              {{ (booking.asset_reference as Charger).addressString }}</v-card-subtitle
            >
          </div>
          <div class="flex-grow-1 flex-shrink-1" style="width: 100%">
            <div class="d-flex justify-start justify-md-end">
              <div
                class="d-flex align-end flex-column"
                :class="{ 'pt-2': mobile }"
                style="width: 100%"
                :style="mobile ? 'max-width: 100%' : 'max-width: 220px'"
              >
                <OutlinedBlockBtn
                  :shape="mobile ? 'box' : 'pill'"
                  class="mb-1"
                  @click="() => (showEditDialog = true)"
                >
                  Edit booking
                </OutlinedBlockBtn>
                <ElevatedBlockBtn
                  color="pwt_pink_20"
                  :shape="mobile ? 'box' : 'pill'"
                  class="mt-1 text-pwt_red"
                  flat
                  :loading="cancelling"
                  @click="() => (showCancelConfirmationDialog = true)"
                >
                  Cancel booking
                </ElevatedBlockBtn>
              </div>
            </div>
          </div>
        </div>
        <v-card-text>
          <!-- basic details section -->
          <ul class="text-body-1" style="list-style: none; padding-left: unset">
            <li>data provided by {{ (booking.asset_reference as Charger).dataSrcDisplayStr }}</li>
            <li>{{ (booking.asset_reference as Charger).evses.length }} devices</li>
            <li>
              {{ (booking.asset_reference as Charger).simpleDisplayRating }}
            </li>
          </ul>
        </v-card-text>
      </v-card>
      <v-card-title>{{ booking.formattedStartDate }}</v-card-title>
      <v-card-text>
        <v-row no-gutters>
          <!-- booking  start time column -->
          <v-col cols="6" class="pr-2">
            <v-card color="pwt_alabaster" flat rounded="lg">
              <v-card-text class="d-flex flex-column">
                <span class="text-caption"> Booking starts at </span>
                <span class="text-body-1"> {{ booking.formattedStartTime }} </span>
              </v-card-text>
            </v-card>
          </v-col>
          <!-- booking end time column -->
          <v-col cols="6" class="pl-2">
            <v-card color="pwt_alabaster" flat rounded="lg">
              <v-card-text class="d-flex flex-column">
                <span class="text-caption"> Booking ends at </span>
                <span class="text-body-1"> {{ booking.formattedEndTime }} </span>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-title> Details </v-card-title>
      <v-card-text>
        <DeviceCard
          v-if="evseData"
          flat
          :device="evseData"
          :bookable-device-data="
            (booking.asset_reference as Charger).bookableEVSEDisplayData.find(
              (bookableData) => bookableData.evse_id === evseData!.uid,
            )
          "
          color="pwt_alabaster"
        />
      </v-card-text>
    </div>
    <!-- unknowing booking type -->
    <div v-else-if="booking"></div>
    <!-- empty state -->
    <div v-else></div>

    <!-- action dialog modals-->
    <AlertPopupWindow
      :activator="showCancelConfirmationDialog"
      :title="cancelBookingAlertData.title"
      :message="cancelBookingAlertData.message"
      @close="
        () => {
          showCancelConfirmationDialog = false
        }
      "
      @continue="cancelBooking"
      :display-do-not-show-again-input="false"
    />
    <GenericDialog
      :activator="showEditDialog"
      @close="() => (showEditDialog = false)"
      @after-leave="cleanUpEditBooking"
    >
      <v-card v-if="booking" class="pt-10">
        <v-card-title class="pb-10 text-center"> Edit your booking </v-card-title>
        <v-card-text>
          <v-row no-gutters>
            <v-col cols="12" sm="6" class="pr-2">
              <v-card>
                <v-card-title> from </v-card-title>
                <v-card-text>
                  <DatePickerInput
                    identifier="start"
                    label="Date"
                    :initial-value="booking.start_date"
                    @update="handleUpdateDate"
                  />
                  <TimePickerInput
                    label="Time"
                    identifier="start"
                    :initial-value="DateTime.fromJSDate(booking.start_date).toFormat('HH:mm')"
                    @update="handleUpdateTime"
                  />
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" class="pl-2">
              <v-card>
                <v-card-title> to </v-card-title>
                <v-card-text>
                  <DatePickerInput
                    identifier="end"
                    label="Date"
                    :initial-value="booking.end_date"
                    @update="handleUpdateDate"
                  />
                  <TimePickerInput
                    label="Time"
                    identifier="end"
                    :initial-value="DateTime.fromJSDate(booking.end_date).toFormat('HH:mm')"
                    @update="handleUpdateTime"
                  />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text class="d-flex align-center justify-center pa-5">
          <template v-if="error">
            <v-icon color="pwt_red" size="large"> mdi-alert-octagon-outline </v-icon>
            <span class="ml-2 text-pwt_red">{{ error }}</span>
          </template>
        </v-card-text>
        <v-card-actions class="justify-center pa-5">
          <OutlinedBtn @click="showEditDialog = false"> Cancel </OutlinedBtn>
          <ElevatedBtn
            @click="handleUpdateBooking"
            :disabled="!dirty || !!error"
            :loading="updating"
          >
            Save
          </ElevatedBtn>
        </v-card-actions>
      </v-card>
      <!-- placeholder card -->
      <v-card v-else></v-card>
    </GenericDialog>
  </v-card>
</template>
